import { createFileRoute, Link } from "@tanstack/react-router";
import heroAsset from "@/assets/hero-v2.jpg.asset.json";
import { useState } from "react";
import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";
import { services } from "@/data/site-content";
import { serviceDetails } from "@/data/service-dropdown-content";
import { button3DClass } from "@/components/button-3d";

export const Route = createFileRoute("/services/")({
  head: () => ({
    meta: [
      { title: "Our Services in India | MG Content Labs" },
      {
        name: "description",
        content:
          "Our services in India: SEO content strategy, content writing, social media optimization, content optimization, AEO, and link building.",
      },
      { property: "og:title", content: "Our Services in India | MG Content Labs" },
      { property: "og:description", content: "SEO content strategy, content writing, SMO, content optimization, AEO, and link building services for B2B and SaaS brands." },
      { property: "og:url", content: "https://mgcontentlabs.com/services" },
      { property: "og:image", content: `https://mgcontentlabs.com${heroAsset.url}` },
      { name: "twitter:image", content: `https://mgcontentlabs.com${heroAsset.url}` },
    ],
    links: [{ rel: "canonical", href: "https://mgcontentlabs.com/services" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Digital Marketing",
          provider: { "@type": "Organization", name: "MG Content Labs" },
          areaServed: "Worldwide",
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Our Services",
            itemListElement: services.map((s) => ({
              "@type": "Offer",
              itemOffered: { "@type": "Service", name: s.title, description: s.summary },
            })),
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: serviceFaqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: ServicesPage,
});

const serviceFaqs: { q: string; a: string }[] = [
  {
    q: "What services do you offer?",
    a: "Six services: SEO content strategy and semantic SEO, content writing and editorial, social media optimization (SMO), content optimization for existing pages, answer engine optimization (AEO), and digital PR and link building.",
  },
  {
    q: "Do you work with B2B and SaaS companies?",
    a: "Yes. Most of our work is B2B digital marketing for SaaS, technology, and professional services brands that sell through considered, research-heavy buying cycles.",
  },
  {
    q: "Can I buy a single digital marketing service instead of a full plan?",
    a: "Yes. Each service can run standalone, though results compound faster when SEO content strategy, content writing, and distribution work together. Book a call and we'll scope the right mix.",
  },
  {
    q: "Do you serve clients outside India?",
    a: "We're based in Delhi and work with clients across India, the US, the UK, and Europe. Pricing is available in INR and USD.",
  },
];

function ServicesPage() {
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div className="min-h-screen bg-white text-brand-deep">
      <SiteNav />
      <header className="bg-brand-muted py-24">
        <div className="mx-auto max-w-7xl px-6">
          <span className="text-xs font-bold uppercase tracking-widest text-brand-accent">Services</span>
          <h1 className="mt-4 max-w-3xl font-serif text-5xl md:text-6xl">
            Our services <span className="italic text-brand-accent">in India.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-brand-deep/70">
            Six of our services — SEO content strategy, content writing, social media
            optimization, content optimization, answer engine optimization, and link building —
            delivered as one coordinated system for B2B and SaaS brands.
          </p>
        </div>
      </header>

      <section className="py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-x-12 gap-y-16 md:grid-cols-2">
            {services.map((s) => {
              const detail = serviceDetails[s.slug];
              const isExpanded = expanded === s.slug;
              return (
                <article key={s.slug} id={s.slug} className="scroll-mt-24 border-t border-brand-deep/10 pt-8">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">
                    {s.eyebrow}
                  </div>
                  <h2 className="mt-3 font-serif text-3xl">{s.title}</h2>
                  <p className="mt-4 text-brand-deep/70">{s.summary}</p>
                  <ul className="mt-6 grid gap-2 text-sm text-brand-deep/70 sm:grid-cols-2">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2">
                        <span className="mt-1 text-brand-accent">→</span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => setExpanded(isExpanded ? null : s.slug)}
                    className="mt-6 inline-block text-sm font-semibold text-brand-accent hover:text-brand-deep"
                    aria-expanded={isExpanded}
                  >
                    {isExpanded ? "Show less ↑" : "Know more →"}
                  </button>

                  {isExpanded && detail && (
                    <div className="mt-8 bg-brand-muted p-6">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">Featured</div>
                      <h3 className="mt-2 font-serif text-xl">{detail.label}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-brand-deep/70">{detail.whatIs}</p>
                      <div className="mt-4 space-y-3">
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">Why it matters</div>
                          <p className="mt-1 text-sm leading-relaxed text-brand-deep/70">{detail.whyItMatters}</p>
                        </div>
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">How you benefit</div>
                          <p className="mt-1 text-sm leading-relaxed text-brand-deep/70">{detail.howYouBenefit}</p>
                        </div>
                      </div>
                      <div className="mt-4">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">Key Offerings</div>
                        <ul className="mt-1 space-y-1 text-sm text-brand-deep/70">
                          {detail.offerings.map((item) => (
                            <li key={item} className="flex items-start gap-2">
                              <span className="mt-1 text-brand-accent">→</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      {detail.whatToExpect && detail.whatToExpect.length > 0 && (
                        <div className="mt-4">
                          <div className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">What to Expect</div>
                          <ul className="mt-1 space-y-1 text-sm text-brand-deep/70">
                            {detail.whatToExpect.map((item) => (
                              <li key={item} className="flex items-start gap-2">
                                <span className="mt-1 text-brand-accent">→</span>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <div className="mt-4">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">How Customers Benefit</div>
                        <p className="mt-1 text-sm leading-relaxed text-brand-deep/70">{detail.howCustomersBenefit}</p>
                      </div>
                      <Link
                        to={detail.ctaLink}
                        className="mt-5 inline-block text-sm font-semibold text-brand-accent hover:text-brand-deep"
                      >
                        {detail.ctaLabel} →
                      </Link>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-brand-deep py-20 text-white">
        <div className="mx-auto flex max-w-5xl flex-col items-center gap-8 px-6 text-center">
          <h2 className="font-serif text-4xl">Not sure where to start?</h2>
          <p className="max-w-xl text-white/70">
            Book a free 30-minute audit and we'll pinpoint the highest-leverage plays for your funnel.
          </p>
          <Link to="/contact" className={button3DClass("accent")}>
            Book a Free Audit
          </Link>
        </div>
      </section>

      <section className="py-24">
        <div className="mx-auto max-w-4xl px-6">
          <h2 className="font-serif text-4xl">Our services FAQ</h2>
          <div className="mt-12 divide-y divide-brand-deep/10">
            {serviceFaqs.map((f) => (
              <div key={f.q} className="py-6">
                <h3 className="font-bold">{f.q}</h3>
                <p className="mt-2 text-sm text-brand-deep/70">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
